import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Flame, Rocket } from "lucide-react";
import { api } from "@/lib/apiClient";
import { JOURNEY_STAGES } from "@/constants/ingredients";
import PopsicleSVG from "@/components/PopsicleSVG";

const REACTIONS = [
  { key: "love", label: "Love It", Icon: Heart, color: "#FF4F5A" },
  { key: "must_try", label: "Must Try", Icon: Flame, color: "#FFC93C" },
  { key: "make_now", label: "Make Now", Icon: Rocket, color: "#2B2B2B" },
];

export default function HallOfFame() {
  const [legacy, setLegacy] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [voted, setVoted] = useState({}); // id -> reaction key
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/hall-of-fame").then((r) => setLegacy(r.data || []));
    api.get("/dream-pops/public").then((r) => setSubmissions(r.data || []));
    setVoted(JSON.parse(localStorage.getItem("pp_votes") || "{}"));
  }, []);

  const totalVotes = (s) => Object.values(s.votes || {}).reduce((a, b) => a + b, 0);

  const filtered = (() => {
    if (filter === "popular") return [...submissions].sort((a, b) => totalVotes(b) - totalVotes(a));
    if (filter === "creative") return [...submissions].sort((a, b) => (b.ingredients?.length || 0) - (a.ingredients?.length || 0));
    if (filter === "recent") return [...submissions];
    if (filter === "testing") return submissions.filter((s) => s.journey_stage && ["testing", "prototype_created", "customer_trial"].includes(s.journey_stage));
    return submissions;
  })();

  const vote = async (id, reaction) => {
    if (voted[id]) return;
    try {
      const { data } = await api.post(`/dream-pops/${id}/vote`, { reaction });
      setSubmissions((arr) => arr.map((s) => s.id === id ? { ...s, votes: data.votes } : s));
      const nv = { ...voted, [id]: reaction };
      setVoted(nv);
      localStorage.setItem("pp_votes", JSON.stringify(nv));
    } catch {}
  };

  return (
    <div data-testid="hof-page" className="section-pad">
      <div className="container-pp">
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Hall of Fame · Innovation Wall</div>
        <h1 className="editorial-h1 text-5xl md:text-7xl mt-3">Dream Pops that became legends.</h1>
        <p className="mt-6 max-w-2xl text-brand-charcoal/70 leading-relaxed">Inventions submitted by our community. Vote on your favourites and track them as they move through our innovation journey.</p>

        {/* Journey Tracker */}
        <div className="mt-14">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-4">Innovation Journey</div>
          <div className="card-soft p-6 overflow-x-auto">
            <ol className="flex items-center gap-4 min-w-max">
              {JOURNEY_STAGES.map((s, i) => (
                <li key={s.key} className="flex items-center gap-4">
                  <div className="flex flex-col items-center text-center">
                    <div className="h-8 w-8 rounded-full border-2 border-brand-charcoal grid place-items-center font-display text-sm">{i + 1}</div>
                    <div className="mt-2 text-xs uppercase tracking-[0.18em] text-brand-charcoal/70 max-w-[100px] leading-tight">{s.label}</div>
                  </div>
                  {i < JOURNEY_STAGES.length - 1 && <div className="w-8 h-px bg-brand-charcoal/30" />}
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-12 flex flex-wrap gap-2" data-testid="hof-filters">
          {[
            { k: "all", l: "Recently Submitted" },
            { k: "popular", l: "Most Voted" },
            { k: "creative", l: "Most Creative" },
            { k: "testing", l: "Under Testing" },
          ].map((f) => (
            <button key={f.k} data-testid={`hof-filter-${f.k}`} onClick={() => setFilter(f.k)} className={`btn-pill text-xs ${filter === f.k ? "btn-dark" : "btn-ghost border border-brand-line"}`}>{f.l}</button>
          ))}
        </div>

        {/* Community submissions */}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hof-grid">
          {filtered.map((s) => {
            const stageIdx = JOURNEY_STAGES.findIndex((j) => j.key === s.journey_stage);
            return (
              <article key={s.id} className="card-elevated overflow-hidden" data-testid={`hof-card-${s.id}`}>
                <div className="aspect-square relative grid place-items-center bg-brand-muted">
                  <div className="absolute inset-6 rounded-full" style={{ background: `radial-gradient(circle, ${s.color_primary}33, transparent 70%)` }} />
                  <PopsicleSVG primary={s.color_primary || "#FF4F5A"} secondary={s.color_secondary || "#FFC93C"} shape={s.shape || "classic"} className="h-3/4 relative" />
                </div>
                <div className="p-5">
                  <div className="font-display text-2xl">{s.pop_name}</div>
                  {s.tagline && <div className="text-xs italic text-brand-charcoal/65 mt-1">"{s.tagline}"</div>}
                  <div className="text-[10px] uppercase tracking-[0.22em] text-brand-red mt-2">By {s.customer_name}</div>
                  {s.ingredients?.length > 0 && (
                    <div className="mt-3 flex h-2 rounded-full overflow-hidden border border-brand-line">
                      {s.ingredients.map((i) => (<div key={i.name} style={{ width: `${i.percent}%`, background: "#FFC93C" }} title={`${i.name} ${i.percent}%`} />))}
                    </div>
                  )}
                  {s.ingredients?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {s.ingredients.slice(0, 4).map((i) => (<span key={i.name} className="text-[10px] uppercase tracking-[0.16em] border border-brand-line rounded-full px-2 py-0.5">{i.name} {i.percent}%</span>))}
                    </div>
                  )}

                  {/* Journey stage badge */}
                  {stageIdx >= 0 && (
                    <div className="mt-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55">Stage</div>
                      <div className="mt-1 flex h-1.5 rounded-full bg-brand-muted overflow-hidden">
                        <div className="bg-brand-charcoal" style={{ width: `${((stageIdx + 1) / JOURNEY_STAGES.length) * 100}%` }} />
                      </div>
                      <div className="text-xs text-brand-charcoal/65 mt-1">{JOURNEY_STAGES[stageIdx].label}</div>
                    </div>
                  )}

                  {/* Voting */}
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {REACTIONS.map((r) => {
                      const did = voted[s.id] === r.key;
                      const count = s.votes?.[r.key] || 0;
                      return (
                        <button key={r.key} data-testid={`vote-${s.id}-${r.key}`} onClick={() => vote(s.id, r.key)} disabled={!!voted[s.id]}
                          className={`flex flex-col items-center gap-1 py-2 rounded-2xl border text-xs ${did ? "border-brand-charcoal bg-brand-yellow/30" : "border-brand-line hover:bg-brand-muted"}`}>
                          <r.Icon size={14} style={{ color: r.color }} />
                          <span className="font-medium">{r.label}</span>
                          <span className="text-[10px] text-brand-charcoal/65">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {submissions.length === 0 && (
          <div className="mt-12 card-soft p-10 text-center">
            <div className="font-display text-3xl">The Innovation Wall is waiting.</div>
            <p className="mt-3 text-brand-charcoal/70 max-w-md mx-auto">Be the first to submit an invention. Your idea could become our next bestseller.</p>
            <Link to="/flavour-creator" className="btn-pill btn-dark mt-6 text-sm" data-testid="hof-empty-cta">Create Your Own Popsicle</Link>
          </div>
        )}

        {/* Legacy editor-picked HoF */}
        {legacy.length > 0 && (
          <div className="mt-24">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-6">Editor's Picks</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {legacy.map((h) => (
                <article key={h.id} className="card-elevated p-6">
                  <div className="aspect-square grid place-items-center bg-brand-muted rounded-2xl overflow-hidden">
                    {h.image_url ? <img src={h.image_url} alt={h.pop_name} className="h-full w-full object-cover" /> : <PopsicleSVG primary={h.color_primary} secondary={h.color_secondary} className="h-2/3" />}
                  </div>
                  <div className="mt-4">
                    <div className="font-display text-2xl">{h.pop_name}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-brand-red mt-1">By {h.creator_name}</div>
                    <p className="mt-2 text-sm text-brand-charcoal/70">{h.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
